<template>
    <div class="player-list-item" :class="{ 'is-current-user': player.is_current_user }">
        <div class="item-rank">{{ player.rank || rank }}</div>

        <div class="player-list-item-content">
            <div class="item-badges-column">
                <a
                    v-if="teamPublicPath(player)"
                    :href="teamPublicPath(player)"
                    class="team-logo-badge-link"
                    @click.stop
                >
                    <img :src="player.team_logo" :alt="player.team_name" class="team-logo-badge" />
                </a>
                <img
                    v-else-if="player.team_logo"
                    :src="player.team_logo"
                    :alt="player.team_name"
                    class="team-logo-badge"
                />
                <div v-if="player.number" class="item-number-badge">#{{ player.number }}</div>
                <div v-if="player.position_slug" class="position-badge" :title="player.position">
                    {{ getPositionAbbreviation(player.position_slug) }}
                </div>
            </div>

            <div class="item-photo-column">
                <div class="item-photo">
                    <img v-if="player.photo_url" :src="player.photo_url" :alt="player.full_name"
                        @error="handleImageError" />
                    <div v-else class="photo-placeholder">
                        {{ getInitials(player.full_name) }}
                    </div>
                </div>
            </div>

            <div class="item-stats-column">
                <div class="item-name-wrapper">
                    <RegisteredBadge :status="player.status" size="sm" />
                    <a v-if="player.url" :href="player.url" class="item-name">{{ player.full_name }}</a>
                    <span v-else class="item-name-text">{{ player.full_name }}</span>
                </div>
                <div class="item-stats-vertical">
                    <div class="stat-row">
                        <span class="stat-label">Игры</span>
                        <span class="stat-value">{{ player.matches_played }}</span>
                    </div>
                    <div class="stat-row">
                        <span class="stat-label">Партии</span>
                        <span class="stat-value">{{ player.sets_played || player.matches_played }}</span>
                    </div>
                    <div v-if="category === 'attack' && player.attack_kills_per_set !== undefined" class="stat-row">
                        <span class="stat-label">Очков за сет</span>
                        <span class="stat-value">{{ formatKillsPerSet(player.attack_kills_per_set) }}</span>
                    </div>
                    <div v-if="category === 'serve' && player.serve_aces_per_set !== undefined" class="stat-row">
                        <span class="stat-label">Очков за сет</span>
                        <span class="stat-value">{{ formatKillsPerSet(player.serve_aces_per_set) }}</span>
                    </div>
                    <div v-if="category === 'block' && player.block_positive_per_set !== undefined" class="stat-row">
                        <span class="stat-label">Очков за сет</span>
                        <span class="stat-value">{{ formatKillsPerSet(player.block_positive_per_set) }}</span>
                    </div>
                    <div v-if="category === 'reception' && player.reception_total" class="stat-row">
                        <span class="stat-label">Поз. прием</span>
                        <span class="stat-value">{{ formatEfficiency(player.reception_positive_pct) }}%</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Формула эффективности для атаки -->
        <template v-if="category === 'attack'">
            <EfficiencyFormula :player="player" :category="category" theme="light" />
        </template>

        <!-- Формула эффективности для подачи -->
        <template v-if="category === 'serve'">
            <EfficiencyFormula :player="player" :category="category" theme="light" />
        </template>

        <!-- Формула эффективности для блока -->
        <template v-if="category === 'block'">
            <EfficiencyFormula :player="player" :category="category" theme="light" />
        </template>

        <!-- Формула эффективности для приема -->
        <template v-if="category === 'reception'">
            <EfficiencyFormula :player="player" :category="category" theme="light" />
        </template>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import EfficiencyFormula from './EfficiencyFormula.vue';
import RegisteredBadge from '@/Components/shared/RegisteredBadge.vue';
import { teamPublicPath } from '@/utils/teamUrl';

const props = defineProps({
    player: {
        type: Object,
        required: true
    },
    category: {
        type: String,
        required: true
    },
    rank: {
        type: Number,
        required: true
    },
    sortBy: {
        type: String,
        default: 'kills'
    }
});

const imageError = ref(false);

const handleImageError = () => {
    imageError.value = true;
};

const getInitials = (fullName) => {
    if (!fullName) return 'Т';
    const parts = fullName.split(' ');
    if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return fullName.charAt(0).toUpperCase();
};

const formatKillsPerSet = (value) => {
    if (value === null || value === undefined) return '0.00';
    return Number(value).toFixed(2);
};

const formatEfficiency = (value) => {
    if (value === null || value === undefined) return '0.00';
    return Number(value).toFixed(2);
};

const getPercentage = (value, total) => {
    if (!total || total === 0) return '0';
    return Number((value / total) * 100).toFixed(1);
};

const getPositionAbbreviation = (positionSlug) => {
    const abbreviations = {
        'setter': 'S',
        'outside_hitter': 'OH',
        'opposite': 'O',
        'middle_blocker': 'MB',
        'libero': 'L'
    };
    return abbreviations[positionSlug] || positionSlug?.charAt(0).toUpperCase() || '';
};
</script>

<style lang="scss" scoped>
@import '../../../scss/tokens';

@mixin badge-common {
    background: rgba(255, 255, 255, 0.95);
    color: #1a1a1a;
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-size: 0.5rem;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(10px);
}


.player-list-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    background: #f8fafc;
    border-radius: 12px;
    border: 1px solid $color-brand-alpha;
    position: relative;
    overflow: visible;

    &.is-current-user {
        background: $color-brand-alpha-soft;
        border-color: $color-brand-border;
        box-shadow: 0 1px 4px $color-brand-alpha;

        .item-rank {
            color: #d4af37;
            opacity: 1;
            background: rgba(255, 249, 230, 0.95);
            border: 1px solid rgba(255, 215, 0, 0.3);
        }
    }

    .item-rank {
        position: absolute;
        top: -0.9rem;
        left: -0.9rem;
        width: 1.8rem;
        height: 1.8rem;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.95);
        border-radius: 50%;
        font-size: 0.7rem;
        font-weight: 600;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
        opacity: 0.8;
        z-index: 10;
    }

    .player-list-item-content {
        display: flex;
        justify-content: space-between;
        align-items: start;

        .item-badges-column {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.5rem;

            .team-logo-badge-link {
                line-height: 0;
                display: block;
            }

            .team-logo-badge {
                width: 32px;
                height: 32px;
                object-fit: contain;
                padding: 0.2rem;
                @include badge-common;
            }

            .item-number-badge {
                @include badge-common;
            }

            .position-badge {
                @include badge-common;
                cursor: help;
            }
        }

        .item-photo-column {
            display: flex;
            align-items: center;
            justify-content: center;

            .item-photo {
                width: 60px;
                height: 80px;

                img {
                    width: 100%;
                    height: 100%;
                    border-radius: 6px;
                    @include person-photo-cover;
                    border: 1.5px solid #ddd;
                }

                .photo-placeholder {
                    width: 100%;
                    height: 100%;
                    border-radius: 6px;
                    background: #ddd;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: #666;
                    border: 1.5px solid #ddd;
                }
            }
        }

        .item-stats-column {
            width: 63%;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;

            .item-name-wrapper {
                display: flex;
                flex-direction: row;
                align-items: center;
                width: 100%;
                gap: 0.5rem;
                min-width: 0;
                max-width: 100%;

                .item-name {
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: #1a1a1a;
                    text-decoration: underline;
                    cursor: pointer;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    min-width: 0;

                    &:hover {
                        color: #4a90e2;
                        text-decoration: underline;
                    }
                }

                .item-name-text {
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: #1a1a1a;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    flex: 1;
                    min-width: 0;
                }
            }

            .item-stats-vertical {
                display: flex;
                flex-direction: row;
                justify-content: space-between;

                .stat-row {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.15rem;
                    min-width: 30%;
                    padding: 0.35rem 0.5rem;
                    background: #fff;
                    border: 1px solid #e0e0e0;
                    border-radius: 4px;

                    .stat-label {
                        font-size: 0.55rem;
                        color: #666;
                        text-transform: uppercase;
                        letter-spacing: 0.3px;
                        line-height: 1;
                    }
                }
            }
        }
    }


    .stat-row-formula {
        align-items: flex-start;

        .formula-box-mini {

            &.formula-kills,
            &.formula-positive,
            &.formula-total {
                background: #77c144;
                border: 1px solid rgba(0, 0, 0, 0.1);
                color: #fff;

                &.formula-positive,
                &.formula-total {
                    background: hsl(0deg 0.76% 55.57% / 60%);
                    ;
                }
            }
        }
    }

    .stat-formula-mini {
        display: flex;
        align-items: center;
        gap: 0.2rem;
        flex-wrap: wrap;
        margin-top: 0.2rem;

        .formula-box-mini {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 1.5rem;
            padding: 0.15rem 0.3rem;
            border-radius: 3px;
            font-size: 0.55rem;
            font-weight: 600;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }

        .formula-text-mini {
            font-size: 0.55rem;
            color: #666;
            white-space: nowrap;
        }

        .formula-value-mini {
            font-size: 0.65rem;
            font-weight: 700;
            color: #1a1a1a;
            margin-left: 0.2rem;
        }
    }

    .stat-mini {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.2rem;
        padding: 0.4rem;
        background: #fff;
        border-radius: 5px;
        min-width: 50px;

        &.highlight {
            background: #e8f4f8;
            border: 1px solid #4a90e2;
        }

        &.stat-mini-block-full {
            width: 100%;
            background: #fff;
            border: 1px solid #e0e0e0;
            padding: 0.75rem;
            margin-top: 0.5rem;
            height: 5.5rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 0.2rem;

            .stat-mini-label,
            .stat-mini-value {
                color: #1a1a1a;
            }

            .stat-mini-label {
                color: #666;
            }
        }

        .stat-mini-label {
            color: #666;
            text-transform: uppercase;
        }

        .stat-mini-value {
            font-weight: 700;
            color: #1a1a1a;
        }
    }
}
</style>
